import React from 'react';
import { useTranslation } from 'react-i18next';
import logo from 'assets/img/logo.png';

// CSS-in-JS styles - Modern Glassmorphism Design
const styles = {
  container: {
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    background: '#0a0d14',
    fontFamily: "'Inter', 'Plus Jakarta Display', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  backgroundLayer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  baseGradient: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom right, #0a0d14 0%, #0f1720 30%, #0d1218 60%, #080b10 100%)',
  },
  // Floating orbs like dashboard
  floatingOrb1: {
    position: 'absolute',
    top: '-20%',
    left: '-10%',
    width: '60vw',
    height: '60vw',
    maxWidth: '800px',
    maxHeight: '800px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.04) 40%, transparent 70%)',
    filter: 'blur(60px)',
    animation: 'float1 20s ease-in-out infinite',
  },
  floatingOrb2: {
    position: 'absolute',
    top: '10%',
    right: '-5%',
    width: '45vw',
    height: '45vw',
    maxWidth: '600px',
    maxHeight: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(74, 222, 128, 0.06) 0%, rgba(34, 197, 94, 0.03) 50%, transparent 70%)',
    filter: 'blur(50px)',
    animation: 'float2 25s ease-in-out infinite',
  },
  floatingOrb3: {
    position: 'absolute',
    bottom: '10%',
    left: '5%',
    width: '30vw',
    height: '30vw',
    maxWidth: '400px',
    maxHeight: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 60%)',
    filter: 'blur(40px)',
    animation: 'float3 18s ease-in-out infinite',
  },
  floatingOrb4: {
    position: 'absolute',
    bottom: '-10%',
    right: '20%',
    width: '35vw',
    height: '35vw',
    maxWidth: '500px',
    maxHeight: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6, 182, 212, 0.05) 0%, transparent 60%)',
    filter: 'blur(50px)',
    animation: 'float1 22s ease-in-out infinite reverse',
  },
  gridPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.03,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px',
  },
  // Glow lines like dashboard
  glowLine1: {
    position: 'absolute',
    top: '30%',
    left: 0,
    width: '100%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(34, 197, 94, 0.1) 20%, rgba(74, 222, 128, 0.15) 50%, rgba(34, 197, 94, 0.1) 80%, transparent 100%)',
    animation: 'pulse 4s ease-in-out infinite',
  },
  glowLine2: {
    position: 'absolute',
    top: '70%',
    left: 0,
    width: '100%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(16, 185, 129, 0.08) 30%, rgba(34, 197, 94, 0.12) 50%, rgba(16, 185, 129, 0.08) 70%, transparent 100%)',
    animation: 'pulse 5s ease-in-out infinite 1s',
  },
  vignette: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(0, 0, 0, 0.4) 100%)',
    pointerEvents: 'none',
  },
  mainContent: {
    position: 'relative',
    zIndex: 1,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  // Glassmorphism Card
  card: {
    maxWidth: '460px',
    width: '100%',
    textAlign: 'center',
    padding: '48px 40px 56px',
    borderRadius: '28px',
    background: 'linear-gradient(135deg, rgba(15, 20, 30, 0.8) 0%, rgba(20, 30, 40, 0.6) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    position: 'relative',
    animation: 'fadeInUp 0.6s ease-out',
    overflow: 'hidden',
  },
  cardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)',
  },
  // Icon container with glow effect
  iconContainer: {
    width: '88px',
    height: '88px',
    margin: '0 auto 24px',
    borderRadius: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    boxShadow: '0 0 40px rgba(34, 197, 94, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  },
  iconGlow: {
    position: 'absolute',
    inset: '-10px',
    borderRadius: '34px',
    background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)',
    filter: 'blur(20px)',
    animation: 'iconPulse 3s ease-in-out infinite',
  },
  icon: {
    width: '44px',
    height: '44px',
    color: '#4ade80',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#ffffff',
    margin: '0 0 12px 0',
    letterSpacing: '-0.02em',
  },
  message: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.65)',
    margin: '0 0 32px 0',
    lineHeight: 1.7,
  },
  errorBox: {
    width: '100%',
    padding: '16px',
    marginBottom: '28px',
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(10px)',
    borderRadius: '14px',
    maxHeight: '100px',
    overflowY: 'auto',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  errorText: {
    fontSize: '12px',
    fontFamily: "'SF Mono', 'Fira Code', 'Monaco', monospace",
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  buttonGroup: {
    display: 'flex',
    gap: '14px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '24px',
  },
  primaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px 32px',
    fontSize: '15px',
    fontWeight: 600,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 50%, #10b981 100%)',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxShadow: '0 8px 30px -4px rgba(74, 222, 128, 0.4)',
    position: 'relative',
    overflow: 'hidden',
  },
  primaryButtonShimmer: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transform: 'translateX(-100%)',
  },
  secondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 32px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.85)',
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  supportText: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.4)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  logoContainer: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    opacity: 0.4,
    transition: 'opacity 0.2s ease',
  },
  logo: {
    width: '24px',
    height: '24px',
    filter: 'grayscale(100%) brightness(1.5)',
  },
};

// Theme configs for different variants
const themes = {
  error: {
    accentColor: 'rgba(251, 191, 36, 0.5)',
    borderColor: 'rgba(251, 191, 36, 0.15)',
    iconBg: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(251, 146, 60, 0.08) 100%)',
    iconBorder: 'rgba(251, 191, 36, 0.25)',
    iconGlow: 'rgba(251, 191, 36, 0.2)',
    iconColor: '#fbbf24',
    errorBoxBorder: '1px solid rgba(251, 191, 36, 0.15)',
    errorTextColor: '#fbbf24',
  },
  connection: {
    accentColor: 'rgba(34, 197, 94, 0.5)',
    borderColor: 'rgba(34, 197, 94, 0.15)',
    iconBg: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)',
    iconBorder: 'rgba(34, 197, 94, 0.25)',
    iconGlow: 'rgba(34, 197, 94, 0.2)',
    iconColor: '#4ade80',
    errorBoxBorder: '1px solid rgba(34, 197, 94, 0.15)',
    errorTextColor: '#4ade80',
  },
};

// Keyframes for animations
const keyframeStyles = `
  @keyframes fadeInUp {
    0% { opacity: 0; transform: translateY(30px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes float1 {
    0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
    25% { transform: translate(50px, -30px) rotate(5deg) scale(1.05); }
    50% { transform: translate(20px, 40px) rotate(-3deg) scale(0.95); }
    75% { transform: translate(-30px, 20px) rotate(3deg) scale(1.02); }
  }
  @keyframes float2 {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(-40px, 50px) rotate(-5deg); }
    66% { transform: translate(60px, -20px) rotate(8deg); }
  }
  @keyframes float3 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-60px, -40px) scale(1.1); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }
  @keyframes iconPulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
  }
`;

// Icons - Modern style
const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
    <path d="M21 3v5h-5"/>
  </svg>
);

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

// Connection/Wifi Off Icon for connection variant
const WifiOffIcon = ({ color = '#4ade80' }) => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23"/>
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9"/>
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <line x1="12" y1="20" x2="12.01" y2="20"/>
  </svg>
);

// Warning Icon for error variant
const WarningIcon = ({ color = '#fbbf24' }) => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

// Info tip icon
const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);

/**
 * FallbackPage - Unified fallback page for errors and connection issues
 * 
 * @param {string} variant - 'error' | 'connection'
 * @param {string} emoji - Emoji to display (optional, has defaults per variant)
 * @param {string} title - Title text
 * @param {string} message - Description message
 * @param {string} errorDetails - Technical error details (dev only)
 * @param {function} onRetry - Retry button callback
 * @param {function} onGoHome - Home button callback (optional)
 * @param {boolean} isLoading - Show loading state on retry button
 * @param {string} retryText - Custom retry button text
 * @param {string} supportText - Footer support text
 */
function FallbackPage({
  variant = 'error',
  emoji,
  title,
  message,
  errorDetails,
  onRetry,
  onGoHome,
  isLoading = false,
  retryText,
  supportText,
}) {
  const { t } = useTranslation();
  const theme = themes[variant] || themes.error;

  // Default values based on variant - using error_boundary.* and connection_lost.* translation keys
  const defaultEmoji = variant === 'connection' ? '📡' : '😵‍💫';
  const defaultTitle = variant === 'connection' 
    ? t("connection_lost.title", "Connexion perdue")
    : t("error_boundary.title", "Oups, un problème est survenu");
  const defaultMessage = variant === 'connection'
    ? t("connection_lost.description", "Impossible de se connecter au serveur. Veuillez réessayer plus tard.")
    : t("error_boundary.message", "Pas de panique, c'est un petit hic temporaire. Vos budgets restent en sécurité.");
  const defaultRetryText = variant === 'connection'
    ? t("connection_lost.retry", "Réessayer")
    : t("error_boundary.cta_retry", "Réessayer");
  const defaultSupportText = variant === 'connection'
    ? t("connection_lost.tip", "Si le problème persiste, vérifiez votre connexion internet.")
    : t("error_boundary.support", "Si le problème persiste, contactez-nous.");

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/#/admin/dashboard';
    }
  };

  return (
    <>
      <style>{keyframeStyles}</style>
      <div style={styles.container}>
        {/* Animated Background - Same as Dashboard */}
        <div style={styles.backgroundLayer}>
          {/* Base gradient */}
          <div style={styles.baseGradient} />
          
          {/* Floating orbs - animated green glows */}
          <div style={styles.floatingOrb1} />
          <div style={styles.floatingOrb2} />
          <div style={styles.floatingOrb3} />
          <div style={styles.floatingOrb4} />
          
          {/* Grid pattern */}
          <div style={styles.gridPattern} />
          
          {/* Glow lines */}
          <div style={styles.glowLine1} />
          <div style={styles.glowLine2} />
          
          {/* Vignette effect */}
          <div style={styles.vignette} />
        </div>

        {/* Main Content */}
        <div style={styles.mainContent}>
          {/* Glassmorphism Card */}
          <div style={{ ...styles.card, border: `1px solid ${theme.borderColor}` }}>
            {/* Top accent line */}
            <div style={{ ...styles.cardAccent, background: `linear-gradient(90deg, transparent, ${theme.accentColor}, transparent)` }} />

            {/* Icon with glow */}
            <div style={{ 
              ...styles.iconContainer, 
              background: theme.iconBg,
              borderColor: theme.iconBorder,
              border: `1px solid ${theme.iconBorder}`,
            }}>
              <div style={{ ...styles.iconGlow, background: `radial-gradient(circle, ${theme.iconGlow} 0%, transparent 70%)` }} />
              {variant === 'connection' ? (
                <WifiOffIcon color={theme.iconColor} />
              ) : (
                <WarningIcon color={theme.iconColor} />
              )}
            </div>

            {/* Title */}
            <h1 style={styles.title}>
              {title || defaultTitle}
            </h1>

            {/* Message */}
            <p style={styles.message}>
              {message || defaultMessage}
            </p>

            {/* Error Details (development only) */}
            {process.env.NODE_ENV !== 'production' && errorDetails && (
              <div style={{ ...styles.errorBox, border: theme.errorBoxBorder }}>
                <pre style={{ ...styles.errorText, color: theme.errorTextColor }}>
                  {errorDetails}
                </pre>
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.buttonGroup}>
              <button
                style={{
                  ...styles.primaryButton,
                  ...(isLoading ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleRetry}
                disabled={isLoading}
                onMouseOver={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 12px 40px -4px rgba(74, 222, 128, 0.5)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 30px -4px rgba(74, 222, 128, 0.4)';
                }}
              >
                {isLoading ? (
                  <>
                    <div style={styles.spinner} />
                    {t("connection_lost.retrying", "Connexion...")}
                  </>
                ) : (
                  <>
                    <RefreshIcon />
                    {retryText || defaultRetryText}
                  </>
                )}
              </button>

              {variant === 'error' && (
                <button
                  style={styles.secondaryButton}
                  onClick={handleGoHome}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                    e.currentTarget.style.boxShadow = '0 8px 24px -4px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <HomeIcon />
                  {t("error_boundary.cta_home", "Retour au tableau de bord")}
                </button>
              )}
            </div>

            {/* Support text with icon */}
            <p style={styles.supportText}>
              <InfoIcon />
              {supportText || defaultSupportText}
            </p>

            {/* Discrete logo */}
            <div style={styles.logoContainer}>
              <img src={logo} alt="Mufyn" style={styles.logo} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FallbackPage;
